// The `evidence/leaderboard` api module — carved from
// `src/lib/api/evidence-leaderboard.ts` (now a pure re-export delegate). This is
// the SECOND of the two api modules the child change's D1.1 names, and with it
// that line's api half is complete.
//
// S1, the transport severance, is the whole mechanical content of the move:
// `@/utils/scribeHTTP`'s two module singletons become
// `getScribeHttpClients().openApiHttp` and `.scribeHttpV2` from the
// `@heidi/api/client` registry. The registry hands back the SAME instances —
// same interceptors, same auth/refresh state — so paths, verbs, query strings,
// bodies and `.data` unwrapping are byte-identical to the pre-move module.
//
// THIS IS THE FIRST LANE USE OF `openApiHttp`, and it is worth naming because
// the sibling carve recorded the opposite: the `evidence/source` api module
// measured `scribeHttpV2` only, so it wrote down that "the second client on the
// registry's clients object is still unexercised by this lane". It is exercised
// now. The split is not incidental — three of these seven fns are the PUBLIC
// leaderboard reads that require no auth and go out over `openApiHttp`, and the
// four authenticated ones go over `scribeHttpV2`. Mixing them would either send
// a scribe JWT to an anonymous endpoint or drop it from an authenticated one.
//
// Both clients are resolved PER CALL rather than destructured at module scope,
// for the correctness reason `transcript-chips.ts` records: the registry is
// populated by `src/utils/scribeHTTP.ts` at factory time, and a module-scope
// read resolves at import time — i.e. whenever a bundler decides to evaluate
// this chunk, which for `_app`-reachable code can be before bootstrap has run.
//
// The two structural client interfaces keep axios off this module's surface, so
// the runtime `axios` import the pre-move file never had is not introduced here
// either.
import { getScribeHttpClients } from '@heidi/api/client';

import type {
  EvidenceReferralLinkResponse,
  LeaderboardPeriod,
  LeaderboardRegion,
  LeaderboardResponse,
  MyEvidenceStats,
  MyReferralsResponse,
  UpdateLeaderboardSettingsRequest,
  UpdateLeaderboardSettingsResponse,
} from '../../contracts';

interface OpenApiHttp {
  get<T>(url: string): Promise<{ data: T }>;
}

interface ScribeHttp {
  get<T>(url: string): Promise<{ data: T }>;
  post<T>(url: string, body?: unknown): Promise<{ data: T }>;
  patch<T>(url: string, body?: unknown): Promise<{ data: T }>;
}

/** The PUBLIC competitions API — no scribe JWT is attached. */
const openApi = (): OpenApiHttp => getScribeHttpClients().openApiHttp;

/** `scribeHttpV2` base is /api/v2/ml-scribe/; scribe JWT injected by interceptor. */
const http = (): ScribeHttp => getScribeHttpClients().scribeHttpV2;

// ============================================================================
// Public API Methods (no auth required)
// ============================================================================

/**
 * Get the queries leaderboard (public, no auth required).
 */
export async function getQueriesLeaderboard(
  region?: LeaderboardRegion,
  period: LeaderboardPeriod = 'monthly',
  limit: number = 50
): Promise<LeaderboardResponse> {
  const params = new URLSearchParams();
  params.append('type', 'evidence_queries');
  if (region) params.append('region', region);
  params.append('limit', limit.toString());

  const { data } = await openApi().get<LeaderboardResponse>(
    `/competitions/periods/${period}?${params.toString()}`
  );
  return data;
}

/**
 * Get the referrals leaderboard (public, no auth required).
 */
export async function getReferralsLeaderboard(
  region?: LeaderboardRegion,
  period: LeaderboardPeriod = 'monthly',
  limit: number = 50
): Promise<LeaderboardResponse> {
  const params = new URLSearchParams();
  params.append('type', 'referrals');
  if (region) params.append('region', region);
  params.append('limit', limit.toString());

  const { data } = await openApi().get<LeaderboardResponse>(
    `/competitions/periods/${period}?${params.toString()}`
  );
  return data;
}

/**
 * Get the leaderboard for a specific competition by slug (public, no auth required).
 *
 * Uses the slug-based competitions API which supports:
 * - Specific instance slugs (e.g., "monthly-evidence-au-2024-02")
 * - Series slugs (e.g., "monthly-evidence-au") which return the current active instance
 */
export async function getCompetitionLeaderboard(
  slug: string,
  limit: number = 50
): Promise<LeaderboardResponse> {
  const params = new URLSearchParams();
  params.append('limit', limit.toString());

  const { data } = await openApi().get<LeaderboardResponse>(
    `/competitions/${encodeURIComponent(slug)}/leaderboard?${params.toString()}`
  );
  return data;
}

// ============================================================================
// Authenticated API Methods
// ============================================================================

/**
 * Generate or retrieve the user's Evidence referral link.
 */
export async function generateEvidenceReferralLink(): Promise<EvidenceReferralLinkResponse> {
  const { data } = await http().post<EvidenceReferralLinkResponse>(
    '/evidence-referrals/link'
  );
  return data;
}

/**
 * Get the list of users referred by the current user.
 */
export async function getMyReferrals(): Promise<MyReferralsResponse> {
  const { data } = await http().get<MyReferralsResponse>(
    '/evidence-referrals/my-referrals'
  );
  return data;
}

/**
 * Get the current user's Evidence stats and rank.
 */
export async function getMyEvidenceStats(
  period: LeaderboardPeriod = 'monthly'
): Promise<MyEvidenceStats> {
  const { data } = await http().get<MyEvidenceStats>(
    `/evidence-referrals/stats?period=${period}`
  );
  return data;
}

/**
 * Update the user's leaderboard visibility preference.
 */
export async function updateLeaderboardSettings(
  request: UpdateLeaderboardSettingsRequest
): Promise<UpdateLeaderboardSettingsResponse> {
  const { data } = await http().patch<UpdateLeaderboardSettingsResponse>(
    '/evidence-referrals/settings',
    request
  );
  return data;
}

// ============================================================================
// Convenience Exports
// ============================================================================

/**
 * The namespace object every consuming hook calls through, kept rather than
 * replaced by the named exports: five app suites name it in a
 * `jest.mock('@/lib/api/evidence-leaderboard', …)` factory, so losing it would
 * turn a behaviour-preserving move into a call-site migration.
 */
export const evidenceLeaderboardApi = {
  getQueriesLeaderboard,
  getReferralsLeaderboard,
  getCompetitionLeaderboard,
  generateEvidenceReferralLink,
  getMyReferrals,
  getMyEvidenceStats,
  updateLeaderboardSettings,
};
